/**
 * Shared Navigation Configuration
 *
 * Centralized navigation item definitions used by:
 * - AppHeader (horizontal nav for xl+)
 * - AppSidebar (vertical nav for md-lg)
 * - Mobile navigation patterns
 *
 * All navigation items are data-only exports (no React) for maximum reusability.
 */

export interface AppNavItem {
  /** Display label for the navigation item */
  label: string;
  /** Router path for navigation */
  path: string;
  /** Whether this item is restricted to admin users only */
  adminOnly: boolean;
  /** Optional Lucide icon component name (for sidebar) */
  icon?: string;
}

/**
 * Complete navigation configuration
 *
 * Ordered for optimal user flow:
 * 1. Core features (Articles, Inspiration) - always visible
 * 2. Admin features - conditionally rendered based on user role
 * 3. Settings - admin only, typically last
 */
export const navItems: AppNavItem[] = [
  {
    label: 'Articles',
    path: '/dashboard',
    adminOnly: false,
    icon: 'FileText',
  },
  {
    label: 'Inspiration',
    path: '/inspiration',
    adminOnly: false,
    icon: 'Lightbulb',
  },
  {
    label: 'Outline Templates',
    path: '/outline-templates',
    adminOnly: true,
    icon: 'FileEdit',
  },
  {
    label: 'Writing Styles',
    path: '/style-guides',
    adminOnly: true,
    icon: 'PenTool',
  },
  {
    label: 'Settings',
    path: '/settings/config',
    adminOnly: true,
    icon: 'Settings',
  },
];

/**
 * Filter navigation items based on user role
 *
 * @param isAdmin - Whether the current user has admin privileges
 * @returns Filtered navigation items visible to the user
 *
 * @example
 * const { isAdmin } = useUserRole();
 * const visibleItems = getVisibleNavItems(isAdmin);
 * // Returns: Articles, Inspiration (for regular users)
 *
 * @example
 * const { isAdmin } = useUserRole();
 * const visibleItems = getVisibleNavItems(isAdmin);
 * // Returns: Articles, Inspiration, Outline Templates, Writing Styles, Settings (for admins)
 */
export function getVisibleNavItems(isAdmin: boolean): AppNavItem[] {
  return navItems.filter(item => !item.adminOnly || isAdmin);
}

/**
 * Check if a path is currently active
 *
 * @param path - Navigation item path to check
 * @param currentPath - Current router location pathname
 * @returns True if the path is active or a parent of a sub-route
 *
 * @example
 * isActiveRoute('/dashboard', location.pathname); // true
 */
export function isActiveRoute(path: string, currentPath: string): boolean {
  return currentPath === path || currentPath.startsWith(`${path}/`);
}
